'use client';
import React, { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import VerifyEmailState from './VerifyEmailState';
import ForgotPasswordForm from './ForgotPasswordForm';
import { useLang } from '@/lib/langContext';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';

type AuthMode = 'login' | 'register' | 'verify' | 'forgot';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const { t, toggleLang, dir } = useLang();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect iOS
    const ua = navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua);
    setIsIOS(ios);

    // Detect if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsInstalled(standalone);

    // Listen for Android/desktop install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setIsInstallable(false);
      setDeferredPrompt(null);
    }
  };

  const features = dir === 'rtl'
    ? [
        { icon: 'BellAlertIcon', text: 'تنبيهات تلقائية قبل انتهاء صلاحية الوثائق', color: 'from-blue-500/20 to-blue-500/10' },
        { icon: 'DocumentCheckIcon', text: 'تتبع جميع أنواع وثائق الأعمال', color: 'from-emerald-500/20 to-emerald-500/10' },
        { icon: 'BuildingOfficeIcon', text: 'إدارة متعددة الفروع والمستخدمين', color: 'from-violet-500/20 to-violet-500/10' },
        { icon: 'ArrowDownTrayIcon', text: 'استيراد وتصدير Excel و PDF', color: 'from-amber-500/20 to-amber-500/10' },
      ]
    : [
        { icon: 'BellAlertIcon', text: 'Automatic alerts before documents expire', color: 'from-blue-500/20 to-blue-500/10' },
        { icon: 'DocumentCheckIcon', text: 'Track all types of business documents', color: 'from-emerald-500/20 to-emerald-500/10' },
        { icon: 'BuildingOfficeIcon', text: 'Multi-branch and multi-user management', color: 'from-violet-500/20 to-violet-500/10' },
        { icon: 'ArrowDownTrayIcon', text: 'Excel & PDF import and export', color: 'from-amber-500/20 to-amber-500/10' },
      ];

  const handleRegistered = (email: string) => {
    setRegisteredEmail(email);
    setMode('verify');
  };

  const isRTL = dir === 'rtl';

  return (
    <div className="min-h-screen flex bg-[#0B1120]" dir={dir}>
      {/* Brand Panel */}
      <div className="hidden lg:flex flex-col w-[480px] xl:w-[540px] relative overflow-hidden flex-shrink-0">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B3E] via-[#0F2060] to-[#0B1120]" />

        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />

        {/* Glow orbs */}
        <div className="absolute top-1/4 start-1/4 w-64 h-64 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute bottom-1/4 end-1/4 w-48 h-48 rounded-full bg-indigo-500/10 blur-3xl" />

        <div className="relative z-10 flex flex-col h-full p-10 xl:p-12">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center backdrop-blur-sm">
              <AppLogo size={22} />
            </div>
            <div>
              <span className="text-white font-700 text-lg block leading-tight">{t.appName}</span>
              <span className="text-blue-300/70 text-[11px] font-500 tracking-wider uppercase">Document Manager</span>
            </div>
          </div>

          {/* Tagline */}
          <div className="mt-14 flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/15 border border-blue-400/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              <span className="text-blue-300 text-xs font-600 tracking-wide">
                {dir === 'rtl' ? 'نظام إدارة الوثائق' : 'Document Management System'}
              </span>
            </div>

            <h1 className="text-white text-[32px] xl:text-[38px] font-800 leading-[1.2] mb-5">
              {dir === 'rtl'
                ? <>احمِ عملك من<br /><span className="text-blue-400">انتهاء صلاحية</span><br />الوثائق</>
                : <>Protect your business<br />from <span className="text-blue-400">document expiry</span><br />risks</>
              }
            </h1>
            <p className="text-blue-200/70 text-[15px] leading-relaxed max-w-sm">
              {t.tagline}
            </p>

            <div className="mt-8 space-y-3">
              {features.map((f, i) => (
                <div key={`feature-${i}`} className="flex items-center gap-3.5 group">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${f.color} border border-white/10 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105`}>
                    <Icon name={f.icon as any} size={17} className="text-white" />
                  </div>
                  <span className="text-blue-100/80 text-[13.5px] leading-snug">{f.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-8">
            {[
              { value: '500+', label: dir === 'rtl' ? 'شركة' : 'Companies' },
              { value: '50K+', label: dir === 'rtl' ? 'وثيقة' : 'Documents' },
              { value: '99%', label: dir === 'rtl' ? 'دقة التنبيه' : 'Alert Accuracy' },
            ].map((stat) => (
              <div key={`stat-${stat.value}`} className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center backdrop-blur-sm">
                <p className="text-white text-[22px] font-800 tabular-nums leading-tight">{stat.value}</p>
                <p className="text-blue-300/70 text-[11px] mt-1 font-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 relative bg-[#F5F6FA]">
        {/* Language toggle */}
        <div className="absolute top-5 end-5">
          <button
            onClick={toggleLang}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border bg-card hover:bg-muted text-[13px] font-500 transition-all shadow-sm"
          >
            <Icon name="LanguageIcon" size={15} />
            {t.switchLang}
          </button>
        </div>

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <AppLogo size={20} />
          </div>
          <span className="font-700 text-lg text-foreground">{t.appName}</span>
        </div>

        <div className="w-full max-w-[420px]">
          {mode === 'verify' ? (
            <VerifyEmailState onBack={() => setMode('login')} email={registeredEmail} />
          ) : mode === 'forgot' ? (
            <ForgotPasswordForm onBack={() => setMode('login')} />
          ) : mode === 'login' ? (
            <LoginForm onSwitchToRegister={() => setMode('register')} onForgotPassword={() => setMode('forgot')} />
          ) : (
            <RegisterForm
              onSwitchToLogin={() => setMode('login')}
              onRegistered={handleRegistered}
            />
          )}

          {/* PWA Install Guide — shown only when not already installed */}
          {!isInstalled && (
            <div className="mt-6 rounded-2xl border border-indigo-200/60 bg-white/80 backdrop-blur-sm shadow-sm overflow-hidden">
              <div className="px-5 py-4">
                {/* Header */}
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Icon name="DevicePhoneMobileIcon" size={16} className="text-indigo-600" />
                  </div>
                  <p className="text-[13.5px] font-700 text-gray-800 leading-snug">
                    {isRTL
                      ? 'استخدم DocAlert كتطبيق على هاتفك' :'Use DocAlert as an App on Your Phone'}
                  </p>
                </div>

                {/* Description */}
                <p className="text-[12px] text-gray-500 leading-relaxed mb-3">
                  {isRTL
                    ? 'يمكنك إضافة DocAlert إلى الشاشة الرئيسية واستخدامه كتطبيق حتى صدور النسخة الرسمية في المتاجر.' :'Add DocAlert to your home screen and use it like an app until the official store release is available.'}
                </p>

                {/* Android/Desktop install button */}
                {isInstallable && (
                  <button
                    onClick={handleInstallClick}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] font-600 transition-colors mb-3"
                  >
                    <Icon name="ArrowDownTrayIcon" size={15} className="text-white" />
                    {isRTL ? 'تثبيت التطبيق' : 'Install App'}
                  </button>
                )}

                {/* iOS instructions */}
                {isIOS && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                    <Icon name="DevicePhoneMobileIcon" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[11.5px] text-blue-700 leading-relaxed">
                      {isRTL
                        ? 'على iPhone: افتح الموقع في Safari، اضغط زر المشاركة، ثم اختر «إضافة إلى الشاشة الرئيسية».' :'On iPhone: Open the website in Safari, tap Share, then choose "Add to Home Screen".'}
                    </p>
                  </div>
                )}

                {/* Android instructions (shown when no native prompt yet) */}
                {!isInstallable && !isIOS && (
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                      <Icon name="DevicePhoneMobileIcon" size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <p className="text-[11.5px] text-emerald-700 leading-relaxed">
                        {isRTL
                          ? 'على Android: افتح الموقع في Chrome، ثم اختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية».' :'On Android: Open the website in Chrome, then choose "Install App" or "Add to Home Screen".'}
                      </p>
                    </div>
                    <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                      <Icon name="DevicePhoneMobileIcon" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <p className="text-[11.5px] text-blue-700 leading-relaxed">
                        {isRTL
                          ? 'على iPhone: افتح الموقع في Safari، اضغط زر المشاركة، ثم اختر «إضافة إلى الشاشة الرئيسية».' :'On iPhone: Open the website in Safari, tap Share, then choose "Add to Home Screen".'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}